/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/**
 * This file includes polyfills needed by Angular and is loaded before the app.
 * You can add your own extra polyfills to this file.
 *
 * This file is divided into 2 sections:
 *   1. Browser polyfills. These are applied before loading ZoneJS and are sorted by browsers.
 *   2. Application imports. Files imported after ZoneJS that should be loaded before your main
 *      file.
 *
 * The current setup is for so-called "evergreen" browsers; the last versions of browsers that
 * automatically update themselves. This includes Safari >= 10, Chrome >= 55 (including Opera),
 * Edge >= 13 on the desktop, and iOS 10 and Chrome on mobile.
 *
 * Learn more in https://angular.io/docs/ts/latest/guide/browser-support.html
 */

/***************************************************************************************************
 * BROWSER POLYFILLS
 */
// Run `npm install --save web-animations-js`.

import 'core-js';
// IE9, IE10 and IE11 requires all of the following polyfills.
/** IE10 and IE11 requires the following for the Reflect API. */
import 'core-js/es/reflect';
// IE10 和IE11对Object、Array、String方法的支持
import '@dm/ie-polyfills/Object.entries';
import '@dm/ie-polyfills/Object.values';
import '@dm/ie-polyfills/Array.from';
import '@dm/ie-polyfills/Array.of';
import '@dm/ie-polyfills/Array.prototype.find';
import '@dm/ie-polyfills/Array.prototype.findIndex';
import '@dm/ie-polyfills/Array.prototype.includes';
import '@dm/ie-polyfills/String.prototype.includes';
import '@dm/ie-polyfills/String.prototype.repeat';
import '@dm/ie-polyfills/String.prototype.startsWith';
import '@dm/ie-polyfills/String.prototype.endsWith';
import '@dm/ie-polyfills/String.prototype.padStart';
import '@dm/ie-polyfills/String.prototype.padEnd';
import '@dm/ie-polyfills/Node.prototype.after';
import '@dm/ie-polyfills/Node.prototype.append';
import '@dm/ie-polyfills/Node.prototype.remove';
import '@dm/ie-polyfills/Node.prototype.replaceWith';
import '@dm/ie-polyfills/NodeList.prototype.forEach';
import '@dm/ie-polyfills/Element.prototype.closest';
import '@dm/ie-polyfills/Element.prototype.matches';
import '@dm/ie-polyfills/MouseEvent';
import '@dm/ie-polyfills/CustomEvent';
import '@dm/web-crypto-utils/shim';
/***************************************************************************************************
 * Zone JS is required by Angular itself.
 * 注意：如果这里不再引入dist目录下的zone，build\installPatch.js里的zone补丁就无效了
 */
import 'zone.js/dist/zone';  // Included with Angular CLI.

// ie 火狐不支持画椭圆的方法
if (!CanvasRenderingContext2D.prototype.ellipse) {
    CanvasRenderingContext2D.prototype.ellipse = function (x, y, radiusX, radiusY, rotation, startAngle, endAngle, anticlockwise): void {
        const radius = radiusX >= radiusY ? radiusX : radiusY;
        const scaleX = radiusX / radius;
        const scaleY = radiusY / radius;
        this.save();
        this.translate(x, y);
        this.rotate(rotation);
        this.scale(scaleX, scaleY);
        this.arc(0, 0, radius, startAngle, endAngle, anticlockwise);
        this.restore();
    };
}

// IE SVG支持classList
const proto = SVGElement.prototype;
if (!('classList' in proto)) {
    Object.defineProperty(proto, 'classList', {
        get() {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const _this: SVGElement = this;
            return {
                contains: (name: string): void => _this.className.baseVal.split(' ').includes(name),
                add(name: string): void {
                    if (!this.contains(name)) {
                        const names = _this.className.baseVal.split(' ').filter((a) => Boolean(a));
                        names.push(name);
                        _this.setAttribute('class', names.join(' '));
                    }
                },
                remove(name: string): void {
                    if (this.contains(name)) {
                        const names = _this.className.baseVal.split(' ').filter((a) => Boolean(a) && a !== name);
                        _this.setAttribute('class', names.join(' '));
                    }
                },
                toggle(name: string): void {
                    this.contains(name) ? this.remove(name) : this.add(name);
                },
            };
        },
    });
}
